import React, { useEffect, useState } from 'react';
import Input from '../../ui/input';
import Label from '../../ui/label';
import Textarea from '../../ui/textarea';
import { validateZone } from '../../../model/ZoneModel'; // Import your Yup validation schema
import { zoneAdd } from '../../../redux/api/zoneAPI'; // Import the function responsible for adding zone data
import Select from '../../ui/select';
import { getSites, siteLayoutGet } from '../../../redux/api/siteAPI';
import Button from '../../ui/button';
import SitePolygonEditor from '../../site/SitePolygonEditor';
import { toast } from 'react-toastify';

import { useTranslation } from 'react-i18next';

const sites = [
    {
        id: 1,
        site: "yard 1"
    },
    {
        id: 2,
        site: "Room 2"
    },
    {
        id: 3,
        site: "Hallway 1"
    },
];

const AddZoneModal = ({ isOpen, onClose }) => {
    const {t} = useTranslation('common');
    const [formData, setFormData] = useState({
        Zone_name: '',
        Risk_level: '',
        Site_ID: '',
        Zone_description: '',
        Polygon_json: null,
        Fill_color: '#f97316',
        Label_position: null,
    });
    const [sitesList, setSitesList] = useState([]);
    const [siteLayout, setSiteLayout] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [formErrors, setFormErrors] = useState({});

    useEffect(() => {
        const fetchInitialData = async () => {
            setLoading(true);
            try {
                const sitesRes = await getSites();
                setSitesList(sitesRes.data.sites);
            } catch (err) {
                setError(err);
                // static
                setSitesList(sites);
            } finally {
                setLoading(false);
            }
        };
        fetchInitialData();
    }, []);

    useEffect(() => {
        const fetchLayout = async () => {
            if (!formData.Site_ID) {
                setSiteLayout(null);
                return;
            }
            const { data } = await siteLayoutGet(formData.Site_ID);
            setSiteLayout(data);
        };
        fetchLayout();
    }, [formData.Site_ID]);
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));
        // Clear error message when user starts typing
        if (formErrors[name]) {
            setFormErrors(prevErrors => ({
                ...prevErrors,
                [name]: ''
            }));
        }
    };

    const closeModal = (refresh) => {

        setFormData({
            Zone_name: '',
            Risk_level: '',
            Site_ID: '',
            Zone_description: '',
            Polygon_json: null,
            Fill_color: '#f97316',
            Label_position: null,
        });
        setSiteLayout(null);
        setFormErrors({});

        onClose(refresh);
    };

    const validateForm = async () => {
        try {
            await validateZone(formData);
            return true; // Form is valid
        } catch (error) {
            const validationErrors = {};
            error.inner.forEach(err => {
                validationErrors[err.path] = err.message;
            });
            setFormErrors(validationErrors);
            return false; // Form is invalid
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const isValid = await validateForm();
        if (isValid) {
            try {
                // If validation successful, proceed with form submission
                const result = await zoneAdd(formData); // Call the addZone function with the form data
                if (result.error) {
                    setError(result.error);
                    toast.error(result.error);
                } else {
                    closeModal(true); // Close modal or perform other actions
                }
            } catch (error) {
                console.error("Error adding zone:", error); // Log any errors
                setError(error);
                toast.error("An unexpected error occurred while adding the zone.");
            }
        }
    };

    return (
        <form data-testid="zone-add-form" onSubmit={handleSubmit}>
            <div className="grid space-x-6 gap-7 max-lg:grid-cols-1 grid-cols-2 w-auto m-2 bg-white max-md:p-3 p-8 rounded-3xl shadow-lg">
                <p className="flex justify-center col-span-full text-xl text-[#4C4C66] font-bold mb-4">{t('add_zone_details')}</p>
                {/* form */}
                <Input
                    required
                    label={t('zone_name')}
                    name="Zone_name"
                    value={formData.Zone_name}
                    onChange={handleChange}
                    placeholder={t('enter_zone_name')}
                    error={formErrors.Zone_name}
                />

                <Select
                    inputId="e2e-zone-risk"
                    selectOnly
                    required
                    isSearchable={false}
                    label={t('risk_level')}
                    name="Risk_level"
                    options={[
                        { label: t("high"), value: 'High' },
                        { label: t("medium"), value: 'Medium' },
                        { label: t("low"), value: 'Low' },
                    ]}
                    // value={{
                    //     value: formData?.Risk_level,
                    //     label: formData?.Risk_level
                    // }}
                    // value={formData.Risk_level}
                    // className='h-10 rounded-xl p-2 bg-[#F4F6FA]'
                    onChange={(option) => setFormData(prevState => ({ ...prevState, Risk_level: option.value }))}
                    placeholder={t('select_risk_level')}
                    error={formErrors.Risk_level}
                />

                <Select
                    inputId="e2e-zone-site"
                    required
                    label={t('site')}
                    name="Site_ID"
                    options={sitesList.map(site => ({ label: site.Name, value: site.Site_ID }))}
                    // value={{
                    //     value: formData?.Site_ID||null,
                    //     label: formData?.Site_ID||null
                    // }}
                    // className='h-10 rounded-xl p-2 bg-[#F4F6FA]'
                    onChange={(option) => setFormData(prevState => ({ ...prevState, Site_ID: option.value }))}
                    placeholder={t('select_site')}
                    error={formErrors.Site_ID}
                />
                <div className='col-span-full '>

                    <Textarea
                        required
                        label={t('description')}
                        name="Zone_description"
                        maxLength={200}
                        value={formData.Zone_description}
                        onChange={handleChange}
                        rows={5}
                        placeholder={t('enter_zone_description')}
                        error={formErrors.Zone_description}
                    />
                </div>
                {formData.Site_ID && (
                    <SitePolygonEditor
                        layout={siteLayout}
                        value={formData.Polygon_json}
                        fillColor={formData.Fill_color}
                        onChange={(polygonJson) => setFormData(prevState => ({ ...prevState, Polygon_json: polygonJson }))}
                        onFillColorChange={(color) => setFormData(prevState => ({ ...prevState, Fill_color: color }))}
                    />
                )}
                <div className='col-span-full flex justify-center mt-10 space-x-5'>
                    <Button type="reset" onClick={()=>closeModal(false)} size="large" color="cancel" >{t('cancel')}</Button>
                    <Button
                        data-testid="zone-add-submit"
                        type="submit"
                        size="large"
                    >{t('add_zone')}</Button>
                </div>
            </div>
        </form>
    );
};

export default AddZoneModal;
